import {
  EndpointService,
  type APIPaginatedResponse,
  type ID,
} from "@/types/api/api";
import type { IListTabResponse, RedeemableApplicable, TabStatus } from "./Tabs";
import {
  useInfiniteQuery,
  type UseInfiniteQueryResult,
  useMutation,
  type UseMutationResult,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { IAPI } from "../APIService";
import { buildQueryKey } from "@/utils/query";
import type { AxiosResponse } from "axios";
import type { IRedeemableAttachedConfiguration } from "./Configurations";
import type {
  IListRedeemables,
  IListRedeemablesParams,
  IMenuItemRestriction,
} from "./Redeemables";
import { RedeemablesTypes } from "@/components/screens/Redeemables/Shared/Types/ListRedeemablesTypes";
import type { DiscountType } from "./Discount";

export interface IListVoucher {
  active: boolean;
  applicable_to: RedeemableApplicable;
  balance: number;
  cap: number;
  code: string;
  email: string;
  id: number;
  identifier: string;
  message: string;
  name: string;
  status: TabStatus;
  applies_to_service_charge?: boolean;
  created_at: string;
  updated_at: string;
}

export interface IVoucher extends IListVoucher {
  is_applicable: boolean;
  applicable_weekdays: number[];
  cap_per_head: number;
  discounted_menu_only: boolean;
  configuration: IRedeemableAttachedConfiguration;
  bill: unknown;
  menu_item_restrictions: Array<IMenuItemRestriction>;
  percentage: number;
  restaurant_id: ID;
  start_date: string;
  start_time: string;
  end_date: string;
  end_time: string;
  timezone: string;
  discount_type: DiscountType;
  nano_id: string;
  recipient_email: string;
  recipient_name: string;
}

export interface IListVoucherParams {
  search?: string;
  isVenueSpecific?: boolean;
  page?: number;
  page_size?: number;
}

export interface IListVoucherResponse
  extends APIPaginatedResponse<IListVoucher> {}

export interface IListVoucherResponseAll {
  pages: Array<IListVoucherResponse>;
  pageParams: number[];
}

export interface IRetrieveVoucherParams {
  id: ID;
}

export interface IDeleteVoucherParams extends IRetrieveVoucherParams {}

export interface ICreateVoucherParams {
  code: string;
  message: string;
  start_date: string;
  end_date: string;
  email: string;
  name: string;
  cap: number;
  active: boolean;
  applicable_weekdays: Array<number>;
}

export interface IUpdateVoucherParams
  extends Omit<ICreateVoucherParams, "cap"> {
  id: ID;
  start_time: string;
  end_time: string;
}

export interface IVoucherService {
  list: (
    params?: IListVoucherParams,
  ) => UseQueryResult<IListVoucherResponse, Error>;
  listAll: (
    params?: IListVoucherParams,
  ) => UseInfiniteQueryResult<IListVoucherResponseAll, Error>;
  retrieve: (params: IRetrieveVoucherParams) => UseQueryResult<IVoucher, Error>;
  create: (
    listParams?: IListRedeemablesParams,
  ) => UseMutationResult<IVoucher, Error, ICreateVoucherParams>;
  update: () => UseMutationResult<IVoucher, Error, IUpdateVoucherParams>;
  partialUpdate: () => UseMutationResult<
    IVoucher,
    Error,
    Partial<IUpdateVoucherParams>
  >;
  delete: () => UseMutationResult<boolean, Error, IDeleteVoucherParams>;
}

export class VoucherService implements IVoucherService {
  api: IAPI;

  constructor(api) {
    this.api = api;
  }

  list(
    params?: IListVoucherParams,
  ): UseQueryResult<IListVoucherResponse, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== undefined || value !== null,
      ),
    );
    const { isVenueSpecific = false, ...restParams } = updatedParams;
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "vouchers", "list"],
        venueID: this.api.selectedVenue,
        params: restParams,
      }),
      queryFn: async () => {
        const {
          data: { results, next, count, previous },
        } = await this.api.get<AxiosResponse<IListVoucherResponse>>(
          "/promotions/vouchers/",
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: isVenueSpecific,
            params: restParams,
          },
        );

        return {
          results,
          next,
          count,
          previous,
        };
      },
    });
  }

  listAll(
    params?: IListVoucherParams,
  ): UseInfiniteQueryResult<IListVoucherResponseAll, Error> {
    const updatedParams = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => value !== undefined || value !== null,
      ),
    );

    const { isVenueSpecific = false, ...restParams } = updatedParams;

    return useInfiniteQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "vouchers", "list-all"],
        venueID: this.api.selectedVenue,
        params: restParams,
      }),
      queryFn: async ({ pageParam = 1 }) => {
        const { data } = await this.api.get<
          AxiosResponse<IListVoucherResponse>
        >("/promotions/vouchers/", {
          endpointService: EndpointService.Gateway,
          venueSpecific: isVenueSpecific,
          params: { ...restParams, page: pageParam, page_size: 50 },
        });

        return data;
      },
      getNextPageParam: (lastPage: IListTabResponse) => {
        if (lastPage.next) {
          const url = new URL(lastPage.next);
          return Number.parseInt(url.searchParams.get("page") || "1", 10);
        }
        return undefined;
      },
      refetchOnMount: true,
      refetchOnWindowFocus: false,
      initialPageParam: 1,
    });
  }

  retrieve(params: IRetrieveVoucherParams): UseQueryResult<IVoucher, Error> {
    return useQuery({
      queryKey: buildQueryKey({
        queryKey: ["redeemables", "vouchers", "retrieve", params.id],
        venueID: this.api.selectedVenue,
        params: params,
      }),
      queryFn: async () => {
        const { data } = await this.api.get<AxiosResponse<IVoucher>>(
          `/promotions/vouchers/${params.id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      enabled: !!params.id,
    });
  }

  create(
    listParams?: IListRedeemablesParams,
  ): UseMutationResult<IVoucher, Error, ICreateVoucherParams> {
    const queryClient = useQueryClient();
    const updatedListParams = Object.fromEntries(
      Object.entries(listParams || {}).filter(
        ([, value]) => value !== undefined || value !== null,
      ),
    );
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "vouchers", "create"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.post<AxiosResponse<IVoucher>>(
          "/promotions/vouchers/",
          {
            params,
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data;
      },
      onMutate: async (newVoucher) => {
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
        const previousVouchers = queryClient.getQueryData<IListRedeemables>(
          buildQueryKey({
            queryKey: ["redeemables", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        );

        if (previousVouchers) {
          queryClient.setQueryData<IListRedeemables>(
            buildQueryKey({
              queryKey: ["redeemables", "list"],
              venueID: this.api.selectedVenue,
              params: updatedListParams,
            }),
            (old) => {
              return {
                ...old,
                results: [
                  ...old.results,
                  {
                    id: -1,
                    active: true,
                    redeemable_type: RedeemablesTypes.Voucher,
                    start_date: newVoucher.start_date,
                    configuration_id: null,
                    end_date: newVoucher.end_date,
                    message: newVoucher?.message || "",
                    code: newVoucher.code,
                  },
                ],
                count: old.count + 1,
                next: old.next,
                previous: old.previous,
              };
            },
          );
        }

        return { previousVouchers };
      },
      onError: (err, previousVouchers, context) => {
        if (context?.previousVouchers) {
          queryClient.setQueryData(
            buildQueryKey({
              queryKey: ["redeemables", "list"],
              venueID: this.api.selectedVenue,
              params: updatedListParams,
            }),
            context.previousVouchers,
          );
        }
      },
      onSettled: () => {
        queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "list"],
            venueID: this.api.selectedVenue,
            params: updatedListParams,
          }),
        });
      },
    });
  }

  update(): UseMutationResult<IVoucher, Error, IUpdateVoucherParams> {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "vouchers", "update"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, ...restParams } = params;
        const { data } = await this.api.patch<AxiosResponse<IVoucher>>(
          `/promotions/vouchers/${id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: restParams,
          },
        );
        return data;
      },
      onMutate: async (newVoucher) => {
        const { id, ...restParams } = newVoucher;
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "vouchers", "retrieve", id],
            venueID: this.api.selectedVenue,
            params: { id },
          }),
        });
        const previousVoucher = await queryClient.getQueryData<IVoucher>(
          buildQueryKey({
            queryKey: ["redeemables", "vouchers", "retrieve", id],
            venueID: this.api.selectedVenue,
            params: { id },
          }),
        );
        if (previousVoucher) {
          await queryClient.setQueryData<IVoucher>(
            buildQueryKey({
              queryKey: ["redeemables", "vouchers", "retrieve", id],
              venueID: this.api.selectedVenue,
              params: { id },
            }),
            (old) => {
              return {
                ...old,
                ...restParams,
              };
            },
          );
        }
        return { previousVoucher };
      },
      onError: async (err, newVoucher, context) => {
        if (context?.previousVoucher) {
          await queryClient.setQueryData(
            buildQueryKey({
              queryKey: ["redeemables", "vouchers", "retrieve", newVoucher.id],
              venueID: this.api.selectedVenue,
              params: { id: newVoucher.id },
            }),
            context.previousVoucher,
          );
        }
      },
      onSettled: async (data, error, variables) => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "vouchers", "retrieve", variables.id],
            venueID: this.api.selectedVenue,
            params: { id: variables.id },
          }),
        });
      },
    });
  }

  partialUpdate(): UseMutationResult<
    IVoucher,
    Error,
    Partial<IUpdateVoucherParams>
  > {
    const queryClient = useQueryClient();
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "vouchers", "update"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { id, ...restParams } = params;
        const { data } = await this.api.patch<AxiosResponse<IVoucher>>(
          `/promotions/vouchers/${id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
            params: restParams,
          },
        );
        return data;
      },
      onMutate: async (newVoucher) => {
        const { id, ...restParams } = newVoucher;
        await queryClient.cancelQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "vouchers", "retrieve", id],
            venueID: this.api.selectedVenue,
            params: { id },
          }),
        });
        const previousVoucher = await queryClient.getQueryData<IVoucher>(
          buildQueryKey({
            queryKey: ["redeemables", "vouchers", "retrieve", id],
            venueID: this.api.selectedVenue,
            params: { id },
          }),
        );
        if (previousVoucher) {
          await queryClient.setQueryData<IVoucher>(
            buildQueryKey({
              queryKey: ["redeemables", "vouchers", "retrieve", id],
              venueID: this.api.selectedVenue,
              params: { id },
            }),
            (old) => {
              return {
                ...old,
                ...restParams,
              };
            },
          );
        }
        return { previousVoucher };
      },
      onError: async (err, newVoucher, context) => {
        if (context?.previousVoucher) {
          await queryClient.setQueryData(
            buildQueryKey({
              queryKey: ["redeemables", "vouchers", "retrieve", newVoucher.id],
              venueID: this.api.selectedVenue,
              params: { id: newVoucher.id },
            }),
            context.previousVoucher,
          );
        }
      },
      onSettled: async (data, error, variables) => {
        await queryClient.invalidateQueries({
          queryKey: buildQueryKey({
            queryKey: ["redeemables", "vouchers", "retrieve", variables.id],
            venueID: this.api.selectedVenue,
            params: { id: variables.id },
          }),
        });
      },
    });
  }

  delete(): UseMutationResult<boolean, Error, IDeleteVoucherParams> {
    return useMutation({
      mutationKey: buildQueryKey({
        queryKey: ["redeemables", "vouchers", "delete"],
        venueID: this.api.selectedVenue,
        params: {},
      }),
      mutationFn: async (params) => {
        const { data } = await this.api.delete<AxiosResponse<string>>(
          `/promotions/vouchers/${params.id}/`,
          {
            endpointService: EndpointService.Gateway,
            venueSpecific: false,
          },
        );
        return data === "";
      },
    });
  }
}
